import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { UnsharpMaskPass } from './UnsharpMaskPass';
import { BrightnessShader } from './shaders/BrightnessShader';
import { ExposureShader } from './shaders/ExposureShader';
import { ShadowShader } from './shaders/ShadowShader';
import { WatermarkPass } from './WatermarkPass';

export class ImageEffects {
  private composer: EffectComposer;
  private renderPass: RenderPass;
  private unsharpMaskPass: UnsharpMaskPass;
  private brightnessPass: ShaderPass;
  private exposurePass: ShaderPass;
  private shadowPass: ShaderPass;
  private watermarkPass: WatermarkPass;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private effectStates: {
    sharpening: boolean;
    lighting: boolean;
    exposure: boolean;
    shadows: boolean;
  } = {
    sharpening: false,
    lighting: false,
    exposure: false,
    shadows: false,
  };
  private globalEnabled: boolean = true;
  private watermarkEnabled: boolean = true;

  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera, renderer: THREE.WebGLRenderer) {
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;

    // Initialize EffectComposer
    this.composer = new EffectComposer(renderer);

    // Add render pass
    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);

    // Initialize UnsharpMaskPass for sharpening
    this.unsharpMaskPass = new UnsharpMaskPass();
    this.composer.addPass(this.unsharpMaskPass);

    // Initialize brightness pass
    this.brightnessPass = new ShaderPass(BrightnessShader);
    this.composer.addPass(this.brightnessPass);

    // Initialize exposure pass
    this.exposurePass = new ShaderPass(ExposureShader);
    this.composer.addPass(this.exposurePass);

    // Initialize shadow pass
    this.shadowPass = new ShaderPass(ShadowShader);
    this.composer.addPass(this.shadowPass);

    // Enable all passes initially
    this.unsharpMaskPass.enabled = false;
    this.brightnessPass.enabled = false;
    this.exposurePass.enabled = false;
    this.shadowPass.enabled = false;

    this.watermarkPass = new WatermarkPass();
    this.watermarkPass.setEnabled(this.watermarkEnabled);
    this.composer.addPass(this.watermarkPass);
  }

  public setSharpening(value: number): void {
    // Convert value from 0-100% to 0-1000 range
    const internalValue = value * 10;

    // Convert to appropriate sharpening parameters
    const radius = 0.5; // Fixed radius
    const amount = internalValue / 100; // Normalize to 0-10 range for stronger effect
    const threshold = 0.1; // Fixed threshold

    this.unsharpMaskPass.uniforms.radius.value = radius;
    this.unsharpMaskPass.uniforms.amount.value = amount;
    this.unsharpMaskPass.uniforms.threshold.value = threshold;
    this.effectStates.sharpening = value > 0;
    this.unsharpMaskPass.enabled = this.globalEnabled && this.effectStates.sharpening;
  }

  public setLighting(value: number): void {
    // Convert value from 0-100% to 0.5-2.0 range
    // 0% = 0.5 (darker), 50% = 1.0 (normal), 100% = 2.0 (brighter)
    const brightness = 0.5 + (value / 100) * 1.5;
    this.brightnessPass.uniforms.brightness.value = brightness;
    this.effectStates.lighting = value !== 50;
    this.brightnessPass.enabled = this.globalEnabled && this.effectStates.lighting;
  }

  public setExposure(value: number): void {
    // Convert value from 0-100% to -2.0 to 2.0 range
    // 0% = -2.0 (darker), 50% = 0.0 (normal), 100% = 2.0 (brighter)
    const exposure = -2.0 + (value / 100) * 4.0;

    // Adjust gamma based on exposure to maintain contrast
    const gamma = 1.0 + (value - 50) / 100;

    this.exposurePass.uniforms.exposure.value = exposure;
    this.exposurePass.uniforms.gamma.value = gamma;
    this.effectStates.exposure = value !== 50;
    this.exposurePass.enabled = this.globalEnabled && this.effectStates.exposure;
  }

  public setShadows(value: number): void {
    // Convert value from 0-100% to -1.0 to 1.0 range
    // 0% = -1.0 (darker shadows), 50% = 0.0 (normal), 100% = 1.0 (lighter shadows)
    const shadowAmount = -1.0 + (value / 100) * 2.0;
    this.shadowPass.uniforms.shadowAmount.value = shadowAmount;
    this.effectStates.shadows = value !== 50;
    this.shadowPass.enabled = this.globalEnabled && this.effectStates.shadows;
  }

  public toggleEffects(): boolean {
    this.globalEnabled = !this.globalEnabled;

    // Update all passes based on their individual states and global enabled state
    this.unsharpMaskPass.enabled = this.globalEnabled && this.effectStates.sharpening;
    this.brightnessPass.enabled = this.globalEnabled && this.effectStates.lighting;
    this.exposurePass.enabled = this.globalEnabled && this.effectStates.exposure;
    this.shadowPass.enabled = this.globalEnabled && this.effectStates.shadows;

    return this.globalEnabled;
  }

  public isEffectsEnabled(): boolean {
    return this.globalEnabled;
  }

  public render(): void {
    this.composer.render();
  }

  public resize(width: number, height: number): void {
    this.composer.setSize(width, height);
  }

  public dispose(): void {
    this.watermarkPass.dispose();
    this.composer.dispose();
  }

  public setWatermarkEnabled(isEnabled: boolean): void {
    this.watermarkEnabled = isEnabled;
    this.watermarkPass.setEnabled(isEnabled);
  }

  public setWatermarkText(name: string, email: string, client?: string): void {
    this.watermarkPass.setText(name, email, client);
  }

  public setWatermarkColor(rgb: string): void {
    this.watermarkPass.setColor(rgb);
  }

  public setWatermarkOpacity(value: number): void {
    this.watermarkPass.setOpacity(value);
  }
}
